import { Button } from "@/components/ui/button";
import { usePricing } from "@/hooks/usePricing";
import { formatPrice, formatDiscount } from "@/config/pricing";

export default function ProductsSection() {
  const pricing = usePricing();

  const products = [
    {
      name: "Le Pix",
      format: "13 x 13 cm",
      pages: "80 pages max",
      features: ["Couverture semi-rigide", "Papier photo premium"],
      price: formatPrice(pricing.pix.price),
      originalPrice: formatPrice(pricing.pix.originalPrice),
      discount: formatDiscount(pricing.pix.discount),
      badge: "Mini",
      image: "/images/products/carre_13.png",
      link: "/create_book?open=1",
    },
    {
      name: "L'Album Carré",
      format: "21 x 21 cm",
      pages: "30 x 30 cm",
      features: ["Couverture rigide", "De 24 à 160 pages"],
      price: formatPrice(pricing.albumCarre.price),
      originalPrice: formatPrice(pricing.albumCarre.originalPrice),
      discount: formatDiscount(pricing.albumCarre.discount),
      badge: "Best-seller",
      image: "/images/products/carre_21.png",
      link: "/create_book?open=2",
    },
    {
      name: "L'Album Paysage",
      format: "A4",
      pages: "A5",
      features: ["Couverture rigide", "De 24 à 160 pages"],
      price: formatPrice(pricing.albumPaysage.price),
      originalPrice: formatPrice(pricing.albumPaysage.originalPrice),
      discount: formatDiscount(pricing.albumPaysage.discount),
      badge: "",
      image: "/images/products/a4_paysage.png",
      link: "/create_book?open=7",
    },
    {
      name: "L'Album PREMIUM",
      format: "20 x 20 cm",
      pages: "29 x 29 cm",
      features: ["Couverture rigide/Ouverture à plat", "Papier photo premium"],
      price: formatPrice(pricing.albumPremium.price),
      originalPrice: formatPrice(pricing.albumPremium.originalPrice),
      discount: formatDiscount(pricing.albumPremium.discount),
      badge: "Premium",
      image: "/images/products/carre_fuji_20.png",
      link: "/create_book?open=8",
    },
    {
      name: "Le YearBook",
      format: "A4",
      pages: "A5",
      features: ["Couverture rigide", "De 24 à 160 pages"],
      price: formatPrice(pricing.yearbook.price),
      originalPrice: formatPrice(pricing.yearbook.originalPrice),
      discount: formatDiscount(pricing.yearbook.discount),
      badge: "",
      image: "/images/products/a4.png",
      link: "/create_book?open=9",
    },
  ];

  // Position de chaque carte dans la grille pour ne laisser aucun trou
  // avec 5 produits : lg = 6 colonnes (chaque carte span 2), les 2 dernières
  // sont centrées ; md = 2 colonnes, la 5e prend toute la largeur.
  const gridClass = (index: number) => {
    const isFourth = index === 3; // 4e carte
    const isFifth = index === 4; // 5e carte
    return [
      "lg:col-span-2",
      isFourth ? "lg:col-start-2" : "",
      isFifth ? "md:col-span-2 lg:col-span-2 lg:col-start-4" : "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const hoverBg = (name: string) => {
    if (name === "Le Pix" || name === "L'Album Carré") return "hover:bg-[#FDF1E2]";
    return "hover:bg-[#FCEAE6]";
  };

  const handleProductClick = (product: (typeof products)[0]) => {
    window.location.href = product.link;
  };

  return (
    <section id="albums" className="py-12 lg:py-16 bg-background scroll-mt-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-8 lg:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground max-w-4xl mx-auto leading-tight">
            Nos <span className="text-gradient">Albums Photo</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez notre gamme complète d'albums photo, conçus pour immortaliser vos plus beaux souvenirs.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 pt-8">
          {products.map((product, index) => (
            <div
              key={index}
              onClick={() => handleProductClick(product)}
              className={`product-hover-effect group relative bg-card rounded-2xl shadow-card hover:shadow-elegant transition-all duration-500 border border-border/50 cursor-pointer ${hoverBg(
                product.name,
              )} ${gridClass(index)}`}
            >
              {/* Badge - Ruban en haut à gauche */}
              {product.badge && (
                <div className="absolute -top-2 -left-2 bg-[#f9bb72] text-white px-4 py-2 rounded-2xl text-xs font-semibold z-10 shadow-xl transform -rotate-12 origin-top-left">
                  {product.badge}
                </div>
              )}

              {/* Discount Badge */}
              <div
                className="absolute -top-6 right-4 px-3 py-2 rounded-full text-sm font-bold z-10 shadow-xl border-2 border-white text-white"
                style={{ backgroundColor: "#f7bbac" }}
              >
                {product.discount}
              </div>

              {/* Product Album Image */}
              <div className="w-full h-64 relative overflow-hidden rounded-t-2xl">
                <img
                  src={product.image}
                  alt={`${product.name} - Album photo ${product.format}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Product Info */}
              <div className="p-6 space-y-4 flex flex-col text-center">
                <h3 className="text-xl font-display font-semibold text-foreground">{product.name}</h3>

                <div className="text-muted-foreground">
                  <p>
                    {product.format} • {product.pages}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-2 min-h-[50px] flex flex-col items-center">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-muted-foreground">
                      <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: "#f9bb72" }}></div>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="flex flex-col items-center justify-center pt-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">À partir de</span>
                    <span className="text-2xl font-bold text-foreground">{product.price}</span>
                  </div>
                  {product.originalPrice && (
                    <span className="text-lg text-muted-foreground line-through">{product.originalPrice}</span>
                  )}
                </div>

                {/* CTA Button */}
                <div className="pt-4">
                  <Button
                    variant="rainbow"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(product);
                    }}
                  >
                    Choisir
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
